<script>
    import {
        DxDataGrid,
        DxColumn,
        DxFilterRow,
        DxHeaderFilter,
        DxFilterPanel,
        DxSearchPanel,
        DxPager,
        DxPaging,
        DxExport,
        DxToolbar,
        DxItem
    } from 'devextreme-vue/data-grid';
    import sensorsService from '@/services/sensors.service';
    import ModalService from '@/services/core/modal.service';
    import TinyPumperStatusCell from '@/components/tinyPumper/TinyPumperStatusCell';
    import TinyPumperWatchListPicker from '@/components/tinyPumper/TinyPumperWatchListPicker';
    import TinyPumperHealthReportTotals from '@/components/tinyPumper/TinyPumperHealthReportTotals';
    import HealthCheckStatusModal from '@/components/modals/healthCheck/HealthCheckStatusModal';
    import HealthCheckManageWatchlistsModal from '@/components/modals/healthCheck/HealthCheckManageWatchlistsModal';
    import FormCheckbox from '@/components/shared/form/FormCheckbox';

    /**
     * The per-device fleet view. Server-side: `remote-operations` is on and the store is a DevExtreme
     * remote store, so paging, sorting and filtering are the database's job.
     */
    export default {
        name: 'TinyPumperFleetGrid',
        props: {
            showTotals: {
                type: Boolean,
                default: true
            }
        },
        components: {
            DxDataGrid,
            DxColumn,
            DxFilterRow,
            DxHeaderFilter,
            DxFilterPanel,
            DxSearchPanel,
            DxPager,
            DxPaging,
            DxExport,
            DxToolbar,
            DxItem,
            TinyPumperStatusCell,
            TinyPumperWatchListPicker,
            TinyPumperHealthReportTotals,
            FormCheckbox
        },
        data() {
            return {
                dataSource: sensorsService.healthOverviewDataSource(null),
                totals: [],
                selectedWatchlist: null
            };
        },
        methods: {
            loadTotals() {
                if (!this.showTotals) return Promise.resolve();

                return sensorsService.healthOverviewTotals(this.selectedWatchlist?.id || null).then(totals => {
                    this.totals = totals ?? [];
                });
            },
            reload() {
                this.dataSource = sensorsService.healthOverviewDataSource(this.selectedWatchlist?.id || null);
                return this.loadTotals();
            },
            levelconLink(item) {
                return sensorsService.levelconDashboardLink(item.levelconId);
            },
            updateSelectedWatchlist(value) {
                this.selectedWatchlist = value;
                this.reload();
            },
            openStatus(item) {
                ModalService.show(HealthCheckStatusModal, {
                    sensorId: item.id,
                    assetName: item.assetName
                });
            },
            openWatchlistModal() {
                const isNew = !this.selectedWatchlist?.id;

                ModalService.show(HealthCheckManageWatchlistsModal, {
                    isNew,
                    watchlist: !isNew ? this.selectedWatchlist : null,
                    onSaveSuccess: () => {
                        this.$refs.watchlistPicker?.loadWatchlists();
                        this.reload();
                    },
                    onDeleteSuccess: () => {
                        this.selectedWatchlist = null;
                        this.$refs.watchlistPicker?.loadWatchlists();
                        this.reload();
                    }
                });
            },
            async toggleHoldOn(sensorId, holdOn) {
                await sensorsService.holdOn(sensorId, holdOn);
                this.$refs.fleetGrid?.instance?.refresh();
            }
        },
        mounted() {
            this.loadTotals();
        }
    };
</script>

<template>
    <div>
        <TinyPumperHealthReportTotals v-if="showTotals" :totals="totals" class="mb-3" />
        <div id="tiny-pumper-grid-container" style="position: relative">
            <DxDataGrid
                ref="fleetGrid"
                class="admin-grid"
                :data-source="dataSource"
                :remote-operations="true"
                :row-alternation-enabled="true"
                :column-auto-width="true"
                :allow-column-reordering="true"
                :show-borders="false"
                :word-wrap-enabled="true"
            >
                <DxToolbar>
                    <DxItem location="before">
                        <div class="d-flex flex-wrap align-items-center gap-3 mb-3">
                            <label class="mb-0 fw-bold text-nowrap">Watchlist:</label>
                            <TinyPumperWatchListPicker
                                ref="watchlistPicker"
                                :modelValue="selectedWatchlist"
                                @update:modelValue="value => updateSelectedWatchlist(value)"
                                style="min-width: 220px"
                            />
                            <button class="btn btn-outline-primary text-nowrap h-100" @click="() => openWatchlistModal()">
                                {{ selectedWatchlist?.id ? 'Edit' : 'New' }}
                            </button>
                        </div>
                    </DxItem>
                    <DxItem name="exportButton" />
                    <DxItem name="searchPanel" />
                </DxToolbar>
                <DxExport :enabled="true" />
                <DxFilterRow :visible="true" />
                <DxHeaderFilter :visible="true" />
                <DxFilterPanel :visible="true" />
                <DxSearchPanel :visible="true" :width="300" placeholder="Search..." />
                <DxPaging :page-size="20" />
                <DxPager :allowed-page-sizes="[20, 50, 100, 250]" :show-page-size-selector="true" />
                <DxColumn
                    alignment="right"
                    cell-template="actionsCell"
                    css-class="px-3"
                    :allow-sorting="false"
                    :allow-filtering="false"
                    :allow-search="false"
                />
                <DxColumn data-field="companyName" caption="Company" />
                <DxColumn data-field="batteryName" caption="Facility" />
                <DxColumn data-field="assetName" caption="Levelcon Name" cell-template="assetCell" />
                <DxColumn data-field="targetEntityName" caption="Gsb Name" />
                <DxColumn data-field="gatewayName" caption="Gateway" cell-template="gatewayCell" />
                <DxColumn data-field="status" caption="Status" cell-template="statusCell" />
                <DxColumn data-field="writeFrequency" caption="Freq." format="#0 'h'" />
                <DxColumn data-field="variable" caption="Variable" />
                <DxColumn
                    data-field="lastMeasurementDateTimeUtc"
                    caption="Last Measurement (UTC)"
                    data-type="datetime"
                    format="MM/dd/yyyy HH:mm"
                    css-class="font-monospace"
                />
                <DxColumn data-field="holdOn" caption="Hold On" cell-template="holdOnCell" />

                <template #actionsCell="{ data }">
                    <button
                        type="button"
                        class="btn btn-sm btn-outline-primary border-0"
                        title="Open Status Modal"
                        @click="openStatus(data.data)"
                    >
                        <i class="bi bi-zoom-in fs-5" />
                    </button>
                </template>
                <template #assetCell="{ data }">
                    <a
                        class="d-flex align-items-start gap-2"
                        :href="levelconLink(data.data)"
                        target="_blank"
                        rel="noopener"
                        title="Open in Levelcon"
                    >
                        <span>{{ data.data.assetName }}</span>
                        <i class="bi bi-box-arrow-up-right" style="font-size: 0.9rem" />
                    </a>
                </template>
                <template #gatewayCell="{ data }">
                    <div>
                        <TinyPumperStatusCell :status="data.data.gatewayStatus" />
                        <small class="text-muted">{{ data.data.gatewayName }}</small>
                    </div>
                </template>
                <template #statusCell="{ data }">
                    <TinyPumperStatusCell :status="data.value" />
                </template>
                <template #holdOnCell="{ data }">
                    <FormCheckbox
                        switch
                        :modelValue="data.data.holdOn"
                        @change="holdOn => toggleHoldOn(data.data.id, holdOn)"
                        label="Hold On"
                    >
                        {{ data.data.holdOn ? 'Yes' : 'No' }}
                    </FormCheckbox>
                </template>
            </DxDataGrid>
        </div>
    </div>
</template>
